import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpStatusCodeHandler } from '@app/core/http.status.codes';
import { NavigatorService } from '@app/core/navigator.service';
import { DisplayOccasionType } from '@app/models/display-occasion-type';
import { NavigationSectionType } from '@app/models/navigation/navigation-section-type';
import { OrderOccasion } from '@app/models/order-occasion';
import { BasketService } from '@app/api/basket.service';
import { MenuService } from '@app/api/menu.service';
import { takeUntil } from 'rxjs';
import { ProductAddEvent } from '@app/models/product/product-add-event';
import { ProductView } from '@app/models/product/product-view';
import { MenuHelperService } from '@app/shared/services/menu-helper/menu-helper.service';
import { SEOService } from '@app/shared/services/seo.service';
import { SiteService } from '@app/api/site.service';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { Menu } from '@app/models/menu/menu';
import { Product } from '@app/models/menu/product';

@Component({
  template: `
  <ng-template #ProductModal>
    <app-product [occasion]="orderOccasion" [productView]="productView" (onClose)="close()"></app-product>
  </ng-template>
  `
})
export class ProductsDialogEntryComponent extends AndroWebCoreComponent implements AfterViewInit, OnDestroy {
  @ViewChild('ProductModal') private _productModal: TemplateRef<any>;

  public productView: ProductView;
  public orderOccasion: OrderOccasion;

  private _dialogRef: MatDialogRef<any, any>;

  private _seoSet: boolean;
  private _group: string;

  constructor(
    private _router: Router,
    private _seoService: SEOService,
    private _menuService: MenuService,
    private _siteService: SiteService,
    private _basketService: BasketService,
    private _activatedRoute: ActivatedRoute,
    private _navigatorService: NavigatorService,
    private _menuHelperService: MenuHelperService
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this._menuService.menu$
        .pipe(takeUntil(this.destroy$))
        .subscribe((menu: Menu) => {
          if (!menu) {
            return;
          }

          const id = this._activatedRoute.snapshot.paramMap.get('productId');
          const location = this._activatedRoute.snapshot.paramMap.get('location');
          const occasion: string = this._navigatorService.changeTakeAwayToCollection(this._activatedRoute.snapshot.paramMap.get('occasion'));
          this.orderOccasion = DisplayOccasionType[this._navigatorService.toTitleCase(occasion)];
          this._group = this._activatedRoute.snapshot.paramMap.get('displayGroup');

          const product = menu.Products.find((x: Product) => x.Id === id);

          if (!product) {
            return;
          }

          this.productView = {
            name: product.Name,
            description: product.Description,
            href: this._navigatorService.generateRoutePath(location, this.orderOccasion, NavigationSectionType.Menu, this._group),
            imageSource: this.getMenuImageVisualAsset({ data: product, height: 244 }),
            isInDeal: false,
            allergens: this._menuHelperService.getAllergenSummary(product.Allergens),
            variants: product.Variants,
            upselling: product.Upselling,
            save: (event: ProductAddEvent) => this.onSave(event)
          };

          this._seoService.updateProductSeoTitleAndDescription(
              this._siteService.currentSite,
              this.orderOccasion,
              this._group,
              this.productView
          );
          this._seoSet = true;

          this.openProductDialog();
        });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._seoSet) {
      this._seoService.restore();
    }
  }

  public close(): void {
    this._dialogRef.close();
  }

  /**
   * saves the product to the basket
   * @param event
   */
  private async onSave(event: ProductAddEvent): Promise<void> {
    if (!event) {
      return;
    }

    const response = await this._basketService.addProductToBasket(event.item, this.productView.name, this._group, event.itemPrice);

    if (HttpStatusCodeHandler.isSuccessResponse(response)) {
      this.close();
    }
  }

  /**
   * opens the product component in a dialog.
   */
  private openProductDialog(): void {
    const config: MatDialogConfig<any> = {
      height: this.isMobile ? '100%' : '',
      panelClass: ['custom-dialog-two-container', 'product-modal'],
      width: this.isMobile ? '100%' : '450px',
      ariaModal: false
    };

    this._dialogRef = this.openDialog(this._productModal, 'productsModal', config);

    this._dialogRef.afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const params: Params = this._activatedRoute.snapshot.params;

          if (params && (params.productId || params.productName)) {
            const routePath: string = this._navigatorService.generateRoutePath(params.location, params.occasion, NavigationSectionType.Menu, params.displayGroup);
            this._router.navigate([routePath]);
          }

          this.resetSEO();
        });
  }

  /**
   * Restores the title and description
   */
  private resetSEO(): void {
    if (this._seoSet) {
      this._seoService.restore();
    }

    this._seoSet = false;
  }
}
